import { Auth } from 'aws-amplify';
import cx from 'classnames';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { fakeRejectedRequest, getAddressesByPostcode } from '../../api/Location';
import { createUserTrace } from '../../api/Optimise';
import { OnboardingContext } from '../../context/OnboardingContext';
import { usePersistedState } from '../../hooks';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import { getAddressById } from '../../utils/addressHelperUtil';
import { debugLog } from '../../utils/debugLog';
import ensureError from '../../utils/ensureError';

interface Address {
  id: string;
  apiId?: string;
  address: string;
}

interface PostcodeFormValues {
  postcode: string;
}

interface AddressFormValues {
  addressId: string;
}

const AddressContainer: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    setProgressPercentage,
    postcode,
    setPostcode,
    setIsManualAddress,
    addressId,
    setAddressId,
    showAlert,
    setShowAlert,
    alert,
    setAlert,
  } = useContext(OnboardingContext);

  const [isSaving, setIsSaving] = useState(false);

  const postcodeValidationRegex =
    '^([Gg][Ii][Rr]' +
    ' 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y]' +
    '[0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y]' +
    '[0-9]?[A-Za-z])))) ?[0-9][A-Za-z]{2})$';

  const postcodeSchema = Yup.object().shape({
    postcode: Yup.string()
      .nullable()
      .test('len', 'Please enter a UK postcode', (val) => (val?.length ?? 0) >= 6)
      .matches(new RegExp(postcodeValidationRegex), 'Please enter a UK postcode')
      .required('Please enter your postcode'),
  });
  const [addresses, setAddresses] = usePersistedState('addresses', []);

  const addressSchema = Yup.object().shape({
    addressId: Yup.string()
      .oneOf(
        addresses.map((a: Address) => a.apiId || a.id),
        'Please select an address'
      )
      .required('Please select an address'),
  });

  const [isForceApiFailPostcode, setIsForceApiFailPostcode] = usePersistedState('isForceApiFailPostcode', false);
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    setProgressPercentage(10);
  }, [setProgressPercentage]);

  const onSubmitPostcode = async ({ postcode }: PostcodeFormValues) => {
    setAddresses([]);
    setAddressId('');
    setLookupStatus('loading');
    const fakePostcode = /^hp99\s?1ab/i;
    if (fakePostcode.test(postcode)) {
      postcode = 'SW1A 1AA';
      setIsForceApiFailPostcode(true);
      setPostcode(postcode);
    } else {
      setIsForceApiFailPostcode(false);
      setPostcode(postcode.toUpperCase());
    }
    hideAlert();
    try {
      const response = await getAddressesByPostcode(postcode.toUpperCase());
      const { addresses } = response as { addresses: Address[] };
      setAddresses(addresses);
      setLookupStatus('success');
    } catch (error) {
      const message =
        ensureError(error).message ||
        `Error: No addresses found for this postcode: ${postcode}. ` +
          `Note: Only UK mainland addresses are currently supported by ${theme.language.appName}.`;

      debugLog(
        'onboarding-address',
        JSON.stringify({
          t: new Date().toISOString(),
          event: 'AddressContainer.onSubmitPostcode.error',
          postcode: String(postcode),
          errorMessage: ensureError(error).message,
        })
      );

      addAlert('danger', message);

      setPostcode(undefined);
      setLookupStatus('error');
    }
  };

  const onSubmitAddress = async ({ addressId }: AddressFormValues) => {
    try {
      setIsSaving(true);
      const user = await Auth.currentAuthenticatedUser();
      let address;
      if (addressId.includes('|')) {
        address = addressId;
      } else {
        const addressById = getAddressById(addressId, addresses) as Address;
        if (!addressById.id) {
          addAlert(
            'danger',
            'We could not find that address. Please select an address from the list or enter it manually.'
          );
          setIsSaving(false);
          return;
        }
        address = addressById.id;
      }
      const attributes = {
        address,
        'custom:postcode': postcode,
      };

      debugLog(
        'onboarding-address',
        JSON.stringify({
          t: new Date().toISOString(),
          event: 'AddressContainer.onSubmitAddress.updateAttributes',
          addressId,
          postcode: String(postcode),
        })
      );

      await Auth.updateUserAttributes(user, attributes);
      window.scrollTo(0, 0);
      if (isForceApiFailPostcode) {
        await fakeRejectedRequest();
      }
      setIsManualAddress(false);
      setAddressId(addressId);

      await createUserTrace('Address', {
        postcode,
      });

      debugLog(
        'onboarding-address',
        JSON.stringify({
          t: new Date().toISOString(),
          event: 'AddressContainer.onSubmitAddress.success',
          addressId,
        })
      );

      void navigate(`/onboarding/smart-tariff`);
    } catch (err) {
      debugLog(
        'onboarding-address',
        JSON.stringify({
          t: new Date().toISOString(),
          event: 'AddressContainer.onSubmitAddress.error',
          addressId,
          postcode: String(postcode),
          errorName: (err as { name?: string })?.name,
          errorMessage: ensureError(err).message,
        })
      );
      void navigate(`/onboarding/system-unavailable`);
    }
  };

  const onNoAddress = (): void => {
    void navigate(`/onboarding/manual-address`);
  };

  const addAlert = (variant: string, message: string): void => {
    setShowAlert(true);
    setAlert({ variant, message });
  };

  const hideAlert = (): void => {
    setShowAlert(false);
    setAlert(undefined);
  };

  const manualAddressLink = (
    <Form.Group className="not-found-text">
      <div>
        Address not listed?
        <Button variant="link" onClick={onNoAddress} className="p-0 not-found-text ml-1">
          Enter your address manually
        </Button>
      </div>
    </Form.Group>
  );

  return (
    <>
      <h1 className="mb-3">What's your address?</h1>
      <div>
        <p>Please enter the address where your battery is located.</p>
      </div>
      <div>
        <Formik
          validationSchema={postcodeSchema}
          initialValues={{ postcode: postcode || '' }}
          onSubmit={onSubmitPostcode}
          validateOnChange
        >
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              {showAlert && alert && (
                <div>
                  <Alert variant={alert.variant} onClose={() => hideAlert()} dismissible>
                    {alert.message}
                  </Alert>
                </div>
              )}
              <Field name="postcode">
                {({ field, meta }: FieldProps<string>) => (
                  <Form.Group controlId="postcode" className="text-left">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      type="text"
                      value={field.value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        if (postcode) {
                          setAddresses([]);
                          setAddressId('');
                          setPostcode(undefined);
                        }
                        if (lookupStatus !== 'idle') {
                          setLookupStatus('idle');
                        }
                        field.onChange(event);
                      }}
                      onBlur={field.onBlur}
                      disabled={lookupStatus === 'loading'}
                      placeholder="Postcode"
                      className={cx({ error: !!meta.error })}
                    />
                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                  </Form.Group>
                )}
              </Field>

              {!postcode && (
                <Form.Group className="pb-0">
                  <Button variant="primary" className="mb-0 w-100" type="submit">
                    Find my address
                  </Button>
                </Form.Group>
              )}
            </Form>
          )}
        </Formik>
        {lookupStatus !== 'idle' && (
          <div className="text-left">
            {lookupStatus === 'loading' && <LoopSpinner />}
            {lookupStatus === 'success' &&
              (addresses.length > 0 ? (
                <Formik
                  validationSchema={addressSchema}
                  onSubmit={onSubmitAddress}
                  initialValues={{
                    addressId,
                  }}
                  validateOnChange
                  validateOnMount
                >
                  {({ handleSubmit, isValid, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                      <Field name="addressId">
                        {({ field, meta }: FieldProps<string>) => (
                          <Form.Group controlId="addressId">
                            <Form.Label>Select your address</Form.Label>
                            <Form.Control
                              as="select"
                              {...field}
                              className={cx({ error: !!meta.error && meta.touched })}
                            >
                              <optgroup>
                                <option value="" key="address-select">
                                  Select your address
                                </option>
                                {addresses.map(({ id, apiId, address }: Address, index: number) => (
                                  <option key={`address-${index}`} value={apiId || id}>
                                    {address}
                                  </option>
                                ))}
                              </optgroup>
                            </Form.Control>
                            {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                          </Form.Group>
                        )}
                      </Field>
                      {manualAddressLink}
                      <Form.Group>
                        {!isSubmitting && !isSaving ? (
                          <Button
                            variant="primary"
                            disabled={!isValid || isSubmitting || isSaving}
                            className="mb-0 w-100"
                            type="submit"
                          >
                            Continue
                          </Button>
                        ) : (
                          <LoopSpinner />
                        )}
                      </Form.Group>
                    </Form>
                  )}
                </Formik>
              ) : (
                manualAddressLink
              ))}
            {lookupStatus === 'error' && manualAddressLink}
          </div>
        )}
      </div>
    </>
  );
};

export default AddressContainer;
